/**
 * EVERYTHING YOU SEE, drawn with path calls.
 *
 * There is not one image file in this project. Every deer, ridge, cornstalk,
 * snowflake and antler tine below is Canvas 2D geometry. That is a deliberate
 * trade and it bought three things:
 *
 *   · the CSP stays at `img-src 'self' data:` with no sprite sheet, no CDN;
 *   · the whole game is a few hundred KB of JS, which matters to a player on
 *     phone data outside a bar;
 *   · a deer is drawn from the SAME numbers the server hit-tests against
 *     (`ANATOMY` in shared/deer.ts), so the animal on screen and the animal the
 *     server thinks is there cannot drift apart. With a sprite sheet they
 *     would, silently, the first time an artist nudged a frame.
 *
 * ── Performance notes that are not optional ─────────────────────────────────
 * `shadowBlur` and per-frame `createLinearGradient` are both pathological on
 * mobile GPUs (ROUNDTABLE.md, A-20). Neither appears in the per-frame path.
 * Backgrounds are composed once into an offscreen canvas per site and blitted;
 * gradients are built at that time and never again.
 */

import type { SitePalette } from '@shared/sites.js';
import { ANATOMY } from '@shared/deer.js';
import { mulberry32, hashSeed } from '@shared/rng.js';

const TAU = Math.PI * 2;

/** A whitetail's white: throat, belly, tail. The one non-silhouette colour. */
const PELT_WHITE = 'rgba(238,230,214,0.42)';

// ── the animals ─────────────────────────────────────────────────────────────

/**
 * The contact shadow, and it is the single highest-value thing in this file.
 *
 * Before it, every animal was a silhouette floating a few pixels above ground
 * that happened to be the right colour behind it — technically in the right
 * place, and reading as a sticker. A dark ellipse where the hooves are does not
 * "look better" so much as it makes the deer *be somewhere*.
 *
 * It also does real work during a leap: the shadow stays on the ground and
 * shrinks while the animal rises, which is how the eye reads height. Passing
 * `lift` in is the whole reason `poseAt` returns it.
 */
export function drawGroundShadow(
  ctx: CanvasRenderingContext2D,
  x: number,
  groundY: number,
  s: number,
  lift: number,
) {
  if (s < 2) return;
  // A shadow directly under a standing animal is tight and dark; one under an
  // animal at the top of a bound is wide and faint.
  const air = Math.min(1, Math.max(0, lift / (s * 0.5)));
  const spread = 1 + air * 0.7;
  ctx.fillStyle = `rgba(0,0,0,${(0.4 * (1 - air * 0.75)).toFixed(3)})`;
  ctx.beginPath();
  ctx.ellipse(x, groundY, s * 0.4 * spread, s * 0.085 * spread, 0, 0, TAU);
  ctx.fill();
}

export interface DeerDrawOpts {
  /** Stage-pixel position of the hooves. */
  x: number;
  y: number;
  /** Pixels per local unit (hoof-to-shoulder = 1). */
  s: number;
  dir: 1 | -1;
  rack: number;
  gait: number;
  palette: SitePalette;
  /** 0 = alive, 1 = fully collapsed. */
  dead?: number;
  /** Highlight ring for a deer somebody just claimed. */
  tint?: string | null;
}

/**
 * A whitetail, in silhouette with a rim light.
 *
 * Silhouette rather than shaded volume, and not for want of ambition: a
 * high-contrast outline is the shape a player can resolve at the treeline on a
 * phone in a bright bar, which is the actual viewing condition. The rim light
 * along the back is what stops it reading as a hole in the scenery.
 */
export function drawDeer(ctx: CanvasRenderingContext2D, o: DeerDrawOpts) {
  const { s, dir, palette } = o;
  if (s < 1) return;

  ctx.save();
  ctx.translate(o.x, o.y);

  // Collapse: rotate about the hooves and settle. Cheap, and reads exactly like
  // an animal going down rather than a sprite being deleted.
  if (o.dead) {
    const k = easeOut(o.dead);
    ctx.rotate(dir * k * 1.35);
    ctx.translate(0, k * s * 0.1);
    ctx.globalAlpha = 1 - Math.max(0, o.dead - 0.75) * 4;
  }

  ctx.scale(s * dir, s);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  const isBuck = o.rack > 0;
  const legs = o.dead ? 0 : o.gait;

  // ── back legs, drawn first so they sit behind the body ──
  ctx.strokeStyle = palette.animal;
  ctx.lineWidth = 0.075;
  const hooves = [
    drawLeg(ctx, -0.26, -0.58, legs, 0.0, 0.33, 0.31),
    drawLeg(ctx, -0.31, -0.58, legs, 0.12, 0.33, 0.31),
    drawLeg(ctx, 0.24, -0.6, legs, 0.5, 0.31, 0.3),
    drawLeg(ctx, 0.29, -0.6, legs, 0.62, 0.31, 0.3),
  ];
  drawHooves(ctx, hooves, 0.05);

  // ── body ──
  ctx.fillStyle = palette.animal;
  ctx.beginPath();
  ellipse(ctx, ANATOMY.body.dx, ANATOMY.body.dy, ANATOMY.body.rx, ANATOMY.body.ry);
  ctx.fill();

  // Haunch and shoulder, so the body is not a plain egg.
  ctx.beginPath();
  ellipse(ctx, -0.3, -0.6, 0.2, 0.23);
  ctx.fill();
  ctx.beginPath();
  ellipse(ctx, 0.24, -0.63, 0.17, 0.2);
  ctx.fill();

  /**
   * The white belly and throat.
   *
   * Translucent rather than opaque, and inside the body outline rather than on
   * its edge, so the animal is still a silhouette — the shape is what the
   * player reads at the treeline and the shape must not be broken up. This is
   * only doing anything at the two nearest depth bands, which is where a flat
   * silhouette started to look flat.
   */
  if (!o.dead) {
    ctx.fillStyle = PELT_WHITE;
    ctx.beginPath();
    ellipse(ctx, 0.0, -0.44, 0.3, 0.055);
    ctx.fill();
    ctx.fillStyle = palette.animal;
  }

  // ── neck and head ──
  ctx.beginPath();
  ctx.moveTo(0.28, -0.72);
  ctx.lineTo(0.4, -0.98);
  ctx.lineTo(0.54, -1.0);
  ctx.lineTo(0.42, -0.66);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ellipse(ctx, ANATOMY.head.dx, ANATOMY.head.dy, ANATOMY.head.rx * 0.92, ANATOMY.head.ry * 0.95);
  ctx.fill();
  // Muzzle.
  ctx.beginPath();
  ellipse(ctx, 0.63, -0.92, 0.1, 0.065);
  ctx.fill();

  // Ear.
  ctx.beginPath();
  ctx.moveTo(0.44, -1.05);
  ctx.lineTo(0.4, -1.2);
  ctx.lineTo(0.53, -1.09);
  ctx.closePath();
  ctx.fill();

  if (!o.dead) {
    // Throat patch and muzzle band — drawn AFTER the neck, or the neck
    // polygon paints straight over them.
    ctx.fillStyle = PELT_WHITE;
    ctx.beginPath();
    ellipse(ctx, 0.43, -0.82, 0.065, 0.1);
    ctx.fill();
    ctx.beginPath();
    ellipse(ctx, 0.65, -0.93, 0.06, 0.04);
    ctx.fill();

    // The eye. One highlight, and the head stops being a lump.
    ctx.fillStyle = palette.animalLight;
    ctx.beginPath();
    ellipse(ctx, 0.53, -0.99, 0.028, 0.026);
    ctx.fill();
    ctx.fillStyle = palette.animal;
  }

  // ── tail — a whitetail's whole name is in this ──
  ctx.beginPath();
  ctx.moveTo(-0.44, -0.7);
  ctx.quadraticCurveTo(-0.55, -0.78 - (o.dead ? 0 : Math.sin(o.gait * TAU) * 0.03), -0.52, -0.55);
  ctx.lineTo(-0.42, -0.62);
  ctx.closePath();
  ctx.fillStyle = o.dead ? palette.animal : '#e6ded0';
  ctx.fill();
  ctx.fillStyle = palette.animal;

  // ── antlers ──
  if (isBuck) drawRack(ctx, o.rack, palette);

  /**
   * The rim light. A thin stroke along the topline only — spine, neck, brow.
   * This is the single cheapest thing in the renderer that makes the animal
   * look like it is standing in the site's light rather than pasted onto it.
   */
  ctx.strokeStyle = palette.animalLight;
  ctx.lineWidth = 0.045;
  ctx.beginPath();
  ctx.moveTo(-0.42, -0.8);
  ctx.quadraticCurveTo(-0.05, -0.92, 0.3, -0.8);
  ctx.quadraticCurveTo(0.38, -0.95, 0.44, -1.03);
  ctx.stroke();

  if (o.tint) {
    ctx.strokeStyle = o.tint;
    ctx.lineWidth = 0.055;
    ctx.beginPath();
    ellipse(ctx, ANATOMY.body.dx, ANATOMY.body.dy, ANATOMY.body.rx + 0.06, ANATOMY.body.ry + 0.06);
    ctx.stroke();
  }

  ctx.restore();
}

/**
 * A doe. Same construction, no rack, and a visibly different silhouette —
 * lighter build, longer and more upright neck, head carried higher.
 *
 * That difference is the entire fairness of the doe rule, so it is a SHAPE
 * difference and never a colour one (WCAG 1.4.1, ROUNDTABLE.md A-21). A player
 * with any form of colour vision deficiency reads this at exactly the same
 * distance as anyone else.
 */
export function drawDoe(ctx: CanvasRenderingContext2D, o: Omit<DeerDrawOpts, 'rack'>) {
  const { s, dir, palette } = o;
  if (s < 1) return;

  ctx.save();
  ctx.translate(o.x, o.y);
  if (o.dead) {
    const k = easeOut(o.dead);
    ctx.rotate(dir * k * 1.35);
    ctx.translate(0, k * s * 0.1);
    ctx.globalAlpha = 1 - Math.max(0, o.dead - 0.75) * 4;
  }
  ctx.scale(s * dir * 0.93, s * 0.93);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  const legs = o.dead ? 0 : o.gait;
  ctx.strokeStyle = palette.animal;
  ctx.lineWidth = 0.062;
  drawHooves(
    ctx,
    [
      drawLeg(ctx, -0.25, -0.56, legs, 0.0, 0.34, 0.32),
      drawLeg(ctx, -0.3, -0.56, legs, 0.12, 0.34, 0.32),
      drawLeg(ctx, 0.23, -0.58, legs, 0.5, 0.32, 0.31),
      drawLeg(ctx, 0.28, -0.58, legs, 0.62, 0.32, 0.31),
    ],
    0.042,
  );

  ctx.fillStyle = palette.animal;
  ctx.beginPath();
  ellipse(ctx, -0.02, -0.6, 0.4, 0.2);
  ctx.fill();
  ctx.beginPath();
  ellipse(ctx, -0.28, -0.58, 0.17, 0.19);
  ctx.fill();

  if (!o.dead) {
    ctx.fillStyle = PELT_WHITE;
    ctx.beginPath();
    ellipse(ctx, -0.02, -0.44, 0.27, 0.05);
    ctx.fill();
    ctx.fillStyle = palette.animal;
  }

  // The tell: a long, near-vertical neck carrying the head high.
  ctx.beginPath();
  ctx.moveTo(0.24, -0.68);
  ctx.lineTo(0.34, -1.06);
  ctx.lineTo(0.46, -1.06);
  ctx.lineTo(0.38, -0.62);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ellipse(ctx, 0.44, -1.1, 0.15, 0.105);
  ctx.fill();
  ctx.beginPath();
  ellipse(ctx, 0.56, -1.08, 0.085, 0.055);
  ctx.fill();

  if (!o.dead) {
    ctx.fillStyle = PELT_WHITE;
    ctx.beginPath();
    ellipse(ctx, 0.36, -0.92, 0.05, 0.1);
    ctx.fill();
    ctx.fillStyle = palette.animalLight;
    ctx.beginPath();
    ellipse(ctx, 0.47, -1.13, 0.025, 0.023);
    ctx.fill();
    ctx.fillStyle = palette.animal;
  }

  // Two big ears, held out — the other half of the silhouette tell.
  ctx.beginPath();
  ctx.moveTo(0.36, -1.18);
  ctx.lineTo(0.28, -1.36);
  ctx.lineTo(0.44, -1.2);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(0.42, -1.18);
  ctx.lineTo(0.5, -1.34);
  ctx.lineTo(0.5, -1.16);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.moveTo(-0.4, -0.68);
  ctx.quadraticCurveTo(-0.5, -0.76, -0.47, -0.54);
  ctx.lineTo(-0.38, -0.6);
  ctx.closePath();
  ctx.fillStyle = o.dead ? palette.animal : '#e6ded0';
  ctx.fill();

  ctx.strokeStyle = palette.animalLight;
  ctx.lineWidth = 0.042;
  ctx.beginPath();
  ctx.moveTo(-0.38, -0.76);
  ctx.quadraticCurveTo(-0.02, -0.86, 0.26, -0.76);
  ctx.quadraticCurveTo(0.32, -0.98, 0.37, -1.12);
  ctx.stroke();

  if (o.tint) {
    ctx.strokeStyle = o.tint;
    ctx.lineWidth = 0.05;
    ctx.beginPath();
    ellipse(ctx, -0.02, -0.6, 0.46, 0.26);
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Antlers. `points` is the trophy number the player is chasing, so the drawing
 * has to be countable: a 10-pointer must visibly have five tines a side, or the
 * headline score is a number with nothing behind it.
 */
function drawRack(ctx: CanvasRenderingContext2D, points: number, palette: SitePalette) {
  const perSide = Math.max(2, Math.round(points / 2));
  const height = 0.24 + Math.min(0.6, points * 0.033);

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  /**
   * Two passes: a thicker dark pass and a thinner light one on top of it.
   *
   * The single-weight version disappeared entirely against a dark treeline —
   * which is the one moment the rack has to be readable, because the rack is
   * the score. An outlined antler holds its shape against sky, bark and snow
   * alike, for the price of drawing the same paths twice.
   */
  const passes: Array<[string, number]> = [
    ['rgba(0,0,0,0.7)', 1.7],
    [palette.animalLight, 1],
  ];

  for (const [color, weight] of passes) {
    ctx.strokeStyle = color;

    for (const side of [1, -0.55] as const) {
      const baseX = 0.47;
      const baseY = -1.07;
      const tipX = baseX + 0.34 * height * 1.5 * side * 0.9;
      const tipY = baseY - height;

      // Main beam: a shallow S, sweeping up and back over the shoulders.
      ctx.lineWidth = 0.052 * weight;
      ctx.beginPath();
      ctx.moveTo(baseX, baseY);
      ctx.bezierCurveTo(baseX + 0.16 * side, baseY - height * 0.42, tipX - 0.1 * side, tipY + height * 0.3, tipX, tipY);
      ctx.stroke();

      // Tines, spaced along the beam and shortening toward the tip.
      ctx.lineWidth = 0.04 * weight;
      for (let i = 1; i < perSide; i++) {
        const u = i / perSide;
        const bx = baseX + (tipX - baseX) * u + 0.06 * side * Math.sin(u * Math.PI);
        const by = baseY + (tipY - baseY) * u - height * 0.14 * Math.sin(u * Math.PI);
        const len = (0.2 + 0.12 * (1 - u)) * (height / 0.6);
        ctx.beginPath();
        ctx.moveTo(bx, by);
        ctx.quadraticCurveTo(bx + 0.03 * side, by - len * 0.7, bx + 0.05 * side * (1 - u), by - len);
        ctx.stroke();
      }
    }

    // A brow tine on each beam — the detail that makes a rack read as a rack.
    ctx.lineWidth = 0.036 * weight;
    ctx.beginPath();
    ctx.moveTo(0.48, -1.09);
    ctx.quadraticCurveTo(0.62, -1.14, 0.66, -1.26);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0.46, -1.09);
    ctx.quadraticCurveTo(0.56, -1.16, 0.56, -1.3);
    ctx.stroke();
  }

  // The burr, where the beams meet the skull. Small, and it is the difference
  // between antlers growing out of a deer and antlers resting on one.
  ctx.fillStyle = palette.animalLight;
  ctx.beginPath();
  ctx.ellipse(0.47, -1.07, 0.05, 0.035, 0, 0, TAU);
  ctx.fill();
}

/**
 * A two-segment leg on a pendulum. Enough to read as a run at any size.
 * Returns where the hoof ended up, so the caller can put a hoof on it.
 */
function drawLeg(
  ctx: CanvasRenderingContext2D,
  hx: number,
  hy: number,
  phase: number,
  offset: number,
  upper: number,
  lower: number,
): [number, number] {
  const p = (phase + offset) * TAU;
  // Tightened from 0.62/0.85. Wide swings looked like an insect's legs rather
  // than a deer's — real running legs stay much closer under the body, and the
  // silhouette reads better small, which is where it spends most of its time.
  const hip = Math.sin(p) * 0.42;
  const knee = Math.max(0, Math.sin(p + 1.0)) * 0.6;

  const kx = hx + Math.sin(hip) * upper;
  const ky = hy + Math.cos(hip) * upper;
  const a = hip - knee;
  const fx = kx + Math.sin(a) * lower;
  const fy = ky + Math.cos(a) * lower;

  ctx.beginPath();
  ctx.moveTo(hx, hy);
  ctx.lineTo(kx, ky);
  ctx.lineTo(fx, fy);
  ctx.stroke();
  return [fx, fy];
}

/**
 * Hooves: a slightly wider dark cap on the end of each leg.
 *
 * A stroked line ends in a `round` cap of exactly the line's own width, which
 * is why the legs read as wire. Real legs are thin and end in something
 * heavier, and half a millimetre of geometry here is worth more than any
 * amount of extra shading further up the animal.
 */
function drawHooves(ctx: CanvasRenderingContext2D, feet: Array<[number, number]>, r: number) {
  ctx.fillStyle = 'rgba(0,0,0,0.75)';
  for (const [fx, fy] of feet) {
    ctx.beginPath();
    ctx.ellipse(fx, fy, r, r * 0.8, 0, 0, TAU);
    ctx.fill();
  }
}

// ── varmints ────────────────────────────────────────────────────────────────

/** A gopher out of its hole. `up` is 0..1 — it rises, sits, and drops. */
export function drawCritter(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, up: number, dark: string) {
  const rise = easeOut(Math.min(1, up * 3)) * (1 - Math.max(0, (up - 0.82) / 0.18));
  if (rise <= 0) return;
  ctx.save();
  ctx.translate(x, y);

  // The hole, so it does not simply appear out of the ground.
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 1.05, r * 0.36, 0, 0, TAU);
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.rect(-r * 1.4, -r * 3, r * 2.8, r * 3);
  ctx.clip();
  ctx.translate(0, (1 - rise) * r * 1.9);

  ctx.fillStyle = dark;
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.72, r * 0.62, r * 0.8, 0, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(0, -r * 1.5, r * 0.46, r * 0.42, 0, 0, TAU);
  ctx.fill();
  // Ears.
  for (const sx of [-1, 1]) {
    ctx.beginPath();
    ctx.ellipse(sx * r * 0.36, -r * 1.82, r * 0.16, r * 0.2, 0, 0, TAU);
    ctx.fill();
  }
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  for (const sx of [-1, 1]) {
    ctx.beginPath();
    ctx.arc(sx * r * 0.18, -r * 1.55, r * 0.09, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
  ctx.restore();
}

/** The skunk. Same rise, unmistakably different shape, plus the white stripe. */
export function drawSkunk(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, up: number) {
  const rise = easeOut(Math.min(1, up * 3)) * (1 - Math.max(0, (up - 0.82) / 0.18));
  if (rise <= 0) return;
  ctx.save();
  ctx.translate(x, y);
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.beginPath();
  ctx.ellipse(0, 0, r * 1.05, r * 0.36, 0, 0, TAU);
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.rect(-r * 1.8, -r * 3.4, r * 3.6, r * 3.4);
  ctx.clip();
  ctx.translate(0, (1 - rise) * r * 2);

  ctx.fillStyle = '#141014';
  ctx.beginPath();
  ctx.ellipse(0, -r * 0.78, r * 0.72, r * 0.86, 0, 0, TAU);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(r * 0.1, -r * 1.62, r * 0.44, r * 0.4, 0, 0, TAU);
  ctx.fill();
  // The tail up over the back — the reason you know before you shoot.
  ctx.beginPath();
  ctx.moveTo(-r * 0.5, -r * 0.7);
  ctx.quadraticCurveTo(-r * 1.5, -r * 1.7, -r * 0.7, -r * 2.5);
  ctx.quadraticCurveTo(-r * 0.1, -r * 1.9, -r * 0.28, -r * 0.72);
  ctx.closePath();
  ctx.fill();

  ctx.fillStyle = '#f2f0ea';
  ctx.beginPath();
  ctx.moveTo(-r * 0.06, -r * 1.9);
  ctx.quadraticCurveTo(r * 0.16, -r * 1.1, r * 0.02, -r * 0.1);
  ctx.lineTo(-r * 0.24, -r * 0.12);
  ctx.quadraticCurveTo(-r * 0.16, -r * 1.1, -r * 0.3, -r * 1.9);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-r * 0.78, -r * 2.34);
  ctx.quadraticCurveTo(-r * 0.5, -r * 1.7, -r * 0.62, -r * 1.1);
  ctx.lineTo(-r * 0.9, -r * 1.4);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
  ctx.restore();
}

// ── the background ──────────────────────────────────────────────────────────

/**
 * Compose sky, sun, three ridge bands and the ground into one offscreen canvas.
 * Called on a site change or a resize — never per frame. The per-frame cost of
 * the entire background is then a single `drawImage`.
 */
export function paintBackdrop(
  w: number,
  h: number,
  palette: SitePalette,
  cover: string,
  horizonY: number,
  seedText: string,
): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.floor(w));
  c.height = Math.max(1, Math.floor(h));
  const ctx = c.getContext('2d')!;
  const rng = mulberry32(hashSeed(seedText));

  // Sky.
  const sky = ctx.createLinearGradient(0, 0, 0, horizonY);
  sky.addColorStop(0, palette.skyTop);
  sky.addColorStop(1, palette.skyBottom);
  ctx.fillStyle = sky;
  ctx.fillRect(0, 0, w, horizonY + 1);

  /**
   * Stars, thinning toward the horizon.
   *
   * Density comes from the site, not from inspecting how dark `skyTop` is —
   * the hour is a property of the place. They fade out over the lower third of
   * the sky because that is where the light still is at dusk and before dawn,
   * and a star sitting on the treeline looks like a dead pixel.
   */
  if (palette.stars > 0) {
    const count = Math.round(palette.stars * 150 * (w / 420));
    for (let i = 0; i < count; i++) {
      const y = rng.range(0, horizonY);
      const fade = 1 - Math.pow(y / Math.max(1, horizonY), 0.7);
      if (fade <= 0.02) continue;
      const r = rng.range(0.4, 1.5);
      ctx.fillStyle = `rgba(255,252,240,${(rng.range(0.25, 0.95) * fade).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(rng.range(0, w), y, r, 0, TAU);
      ctx.fill();
    }
  }

  // Sun or moon.
  if (palette.disc) {
    const dx = w / 2 + palette.discSx * (w / 2);
    const dy = horizonY + palette.discSy * h * 0.5;
    const glow = ctx.createRadialGradient(dx, dy, 0, dx, dy, w * 0.34);
    glow.addColorStop(0, palette.disc);
    glow.addColorStop(0.12, hexA(palette.disc, 0.55));
    glow.addColorStop(1, hexA(palette.disc, 0));
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, w, horizonY + 1);
    ctx.fillStyle = palette.disc;
    ctx.beginPath();
    ctx.arc(dx, dy, w * 0.045, 0, TAU);
    ctx.fill();
  }

  /**
   * Cloud banks.
   *
   * Second attempt, and the failure is instructive because it is the SAME one
   * the mid-ground scrub made: the first version drew one closed outline with
   * a row of humps along the top and a flat base, which is a scallop border,
   * not a cloud. Five near-identical semicircles in a row read as decoration —
   * you can see the repeat.
   *
   * A cumulus is a MASS. Overlapping ellipses of genuinely different sizes,
   * clustered tighter in the middle and sitting on a common base, have no
   * repeating edge to notice. Tinted from the site's own horizon colour, so
   * the dawn ones are warm and the overcast hollow gets the flat grey it needs.
   *
   * Three tiers, smaller and paler toward the horizon, so the sky has depth
   * rather than being a gradient with shapes floating in it.
   */
  if (palette.clouds > 0) {
    const tiers = 3;
    for (let tier = 0; tier < tiers; tier++) {
      const depth = tier / (tiers - 1);
      const bandY = horizonY * (0.16 + depth * 0.6);
      const scale = 1 - depth * 0.55;
      const alpha = (0.09 + palette.clouds * 0.16) * (1 - depth * 0.3);
      const count = Math.round(((2 + palette.clouds * 3) * (w / 420)) / (1 + depth));
      ctx.fillStyle = hexA(lighten(palette.skyBottom, 0.3), alpha);
      for (let i = 0; i < count; i++) {
        const cx = rng.range(-0.1, 1.1) * w;
        const cy = bandY + rng.range(-0.05, 0.05) * horizonY;
        const cw = w * rng.range(0.14, 0.34) * scale;
        const lobes = 5 + Math.floor(rng.range(0, 4.99));

        /**
         * ONE path, ONE fill. Filling each lobe separately stacks the alpha
         * wherever two overlap, and a translucent shape with brighter discs
         * inside it is the exact look of lens bokeh — which is what this was
         * doing on a wide screen where the banks are biggest. Collecting the
         * subpaths and filling once gives their union at a single opacity,
         * which is a cloud.
         */
        ctx.beginPath();
        for (let k = 0; k < lobes; k++) {
          const u = rng.range(0, 1);
          // Bias toward the middle, so the bank is tall in the centre and thin
          // at the ends, like weather.
          const arc = 1 - Math.abs(u - 0.5) * 2;
          const lx = cx + (u - 0.5) * cw;
          const rx = cw * rng.range(0.1, 0.24) * (0.5 + arc);
          const ry = rx * rng.range(0.42, 0.72);
          const ly = cy - ry * rng.range(0.15, 0.9);
          // `ellipse` draws a line from the current point to its start, so each
          // lobe needs its own moveTo or the cloud is strung together with
          // chords. That point is (lx + rx, ly) — angle zero on the ellipse.
          ctx.moveTo(lx + rx, ly);
          ctx.ellipse(lx, ly, rx, ry, 0, 0, TAU);
        }
        ctx.fill();
      }
    }
  }

  // Three ridge bands, far to near. Each is a sum of sines with a seeded phase,
  // which is enough irregularity to read as terrain and costs nothing.
  const bands = [
    { y: horizonY - h * 0.005, amp: h * 0.035, color: palette.ridge[0], trees: 0 },
    { y: horizonY + h * 0.012, amp: h * 0.028, color: palette.ridge[1], trees: 0.5 },
    { y: horizonY + h * 0.032, amp: h * 0.022, color: palette.ridge[2], trees: 1 },
  ];
  for (const band of bands) {
    const p1 = rng.range(0, TAU);
    const p2 = rng.range(0, TAU);
    const p3 = rng.range(0, TAU);
    ctx.fillStyle = band.color;
    ctx.beginPath();
    ctx.moveTo(0, h);
    for (let x = 0; x <= w; x += 4) {
      const u = x / w;
      const y =
        band.y -
        Math.sin(u * 5.2 + p1) * band.amp -
        Math.sin(u * 11.7 + p2) * band.amp * 0.45 -
        Math.sin(u * 23.1 + p3) * band.amp * 0.2;
      ctx.lineTo(x, y);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();

    /**
     * A rim of light along the crest.
     *
     * One hairline stroke of a lighter version of the band's own colour, and
     * three flat silhouettes become three ridges standing in front of each
     * other. It is the same trick as the deer's topline highlight and it works
     * for the same reason: the eye reads separation from edges, not from fill.
     */
    ctx.strokeStyle = hexA(lighten(band.color, 0.3), 0.55);
    ctx.lineWidth = Math.max(1, h * 0.0015);
    ctx.beginPath();
    for (let x = 0; x <= w; x += 4) {
      const u = x / w;
      const y =
        band.y -
        Math.sin(u * 5.2 + p1) * band.amp -
        Math.sin(u * 11.7 + p2) * band.amp * 0.45 -
        Math.sin(u * 23.1 + p3) * band.amp * 0.2;
      if (x === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();

    // Trees standing on the nearer bands.
    if (band.trees > 0) {
      const count = Math.round(band.trees * 34);
      for (let i = 0; i < count; i++) {
        const x = rng.range(0, w);
        const u = x / w;
        const y =
          band.y -
          Math.sin(u * 5.2 + p1) * band.amp -
          Math.sin(u * 11.7 + p2) * band.amp * 0.45 -
          Math.sin(u * 23.1 + p3) * band.amp * 0.2;
        drawTree(ctx, x, y + 1, h * rng.range(0.018, 0.052) * band.trees, cover, band.color, rng);
      }
    }
  }

  // Ground.
  const ground = ctx.createLinearGradient(0, horizonY, 0, h);
  ground.addColorStop(0, palette.groundFar);
  ground.addColorStop(1, palette.groundNear);
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizonY + h * 0.03, w, h - horizonY);

  /**
   * Ground texture: broad, very low-contrast bands lying ON the perspective
   * curve rather than evenly down the screen, so they compress toward the
   * treeline exactly as the deer do. Without them the ground plane is a
   * vertical gradient, and a vertical gradient reads as a painted wall — which
   * is the same failure the ridge bands had before they got a crest line.
   */
  for (let i = 0; i < 26; i++) {
    const z = rng.range(0.02, 0.98);
    const gy = horizonY + (h - horizonY) * Math.pow(1 - z, 1.7);
    const thick = Math.max(1, (h - horizonY) * 0.012 * (1 - z) + 1);
    const cx = rng.range(-0.1, 1.1) * w;
    const cw = w * rng.range(0.18, 0.55);
    ctx.fillStyle = rng.next() > 0.5
      ? `rgba(255,255,255,${(0.018 + (1 - z) * 0.022).toFixed(3)})`
      : `rgba(0,0,0,${(0.03 + (1 - z) * 0.05).toFixed(3)})`;
    ctx.beginPath();
    ctx.ellipse(cx, gy, cw / 2, thick, 0, 0, TAU);
    ctx.fill();
  }

  /**
   * Mid-ground scatter — clumps of scrub standing on the ground plane between
   * the treeline and the foreground.
   *
   * FOURTH shape, and the three failures are the whole lesson, so they stay
   * written down:
   *
   *   1. Flat ellipses read as PUDDLES.
   *   2. A tidy dome with three straight twigs on top read as a little CAR,
   *      antennae and all — a natural object built out of regular parts.
   *   3. A ragged dome with every peak jittered read as a MOUNTAIN RANGE, in
   *      every one of the five sites at once. Which in hindsight is obvious:
   *      an irregular jagged upper edge over a flat base IS the silhouette of
   *      a ridge, and this file draws three of those on purpose forty pixels
   *      further up the screen.
   *
   * The thing all three have in common is a single continuous outline. A bush
   * does not have one — it is a mass of overlapping rounded lobes with gaps in
   * it, and drawing exactly that is what finally made these read as vegetation.
   * A few blades thrown up out of the top break the last of the silhouette.
   *
   * Drawn BEFORE the haze so distance greys them out, and placed on the same
   * perspective curve the deer use so they sit on the ground rather than
   * floating over it.
   */
  for (let i = 0; i < 22; i++) {
    // Not past 0.85: at 0.95 the clump lands where the ground meets the
    // treeline, and a dark mass sitting on that seam reads as floating.
    const z = rng.range(0.22, 0.85);
    const gy = horizonY + (h - horizonY) * Math.pow(1 - z, 1.7);
    const size = h * 0.05 * (1 / (1 + z * 3.0));
    const x = rng.range(-0.05, 1.05) * w;
    // Just a shadow on whatever ground it stands on, so it is right in every
    // palette without a per-site colour to keep in sync.
    ctx.fillStyle = `rgba(0,0,0,${(0.28 + (1 - z) * 0.28).toFixed(3)})`;

    // The mass: overlapping lobes along a shallow arc, biggest in the middle.
    // One path and one fill, so the overlaps do not stack alpha into brighter
    // discs inside the clump — same reason as the cloud banks above.
    const lobes = 4 + Math.floor(rng.range(0, 3.99));
    ctx.beginPath();
    for (let k = 0; k < lobes; k++) {
      const u = lobes === 1 ? 0.5 : k / (lobes - 1);
      const lx = x + (u - 0.5) * size * 2.2;
      const arc = Math.sin(u * Math.PI);
      const rx = size * rng.range(0.4, 0.72) * (0.55 + arc * 0.6);
      const ry = rx * rng.range(0.6, 0.85);
      const ly = gy - rx * rng.range(0.5, 0.8);
      ctx.moveTo(lx + rx, ly);
      ctx.ellipse(lx, ly, rx, ry, 0, 0, TAU);
    }
    ctx.fill();

    /**
     * Blades out of the top. Curved, not straight — straight was the antennae
     * on the little car. And MANY and short rather than few and tall: two long
     * blades on a small mound is a pair of feelers, six short ones is grass.
     */
    ctx.strokeStyle = `rgba(0,0,0,${(0.22 + (1 - z) * 0.22).toFixed(3)})`;
    ctx.lineWidth = Math.max(1, size * 0.06);
    ctx.lineCap = 'round';
    const blades = 4 + Math.floor(rng.range(0, 3.99));
    for (let k = 0; k < blades; k++) {
      const bx = x + rng.range(-0.9, 0.9) * size;
      const bh = size * rng.range(0.55, 1.05);
      ctx.beginPath();
      ctx.moveTo(bx, gy);
      ctx.quadraticCurveTo(bx + rng.range(-0.15, 0.15) * size, gy - bh * 0.6, bx + rng.range(-0.35, 0.35) * size, gy - bh);
      ctx.stroke();
    }
  }

  // Atmospheric haze, densest at the treeline. This is what sells depth.
  const haze = ctx.createLinearGradient(0, horizonY - h * 0.1, 0, horizonY + h * 0.16);
  haze.addColorStop(0, hexA(palette.haze, 0));
  haze.addColorStop(0.5, palette.haze);
  haze.addColorStop(1, hexA(palette.haze, 0));
  ctx.fillStyle = haze;
  ctx.fillRect(0, horizonY - h * 0.1, w, h * 0.26);

  return c;
}

function drawTree(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  kind: string,
  color: string,
  rng: ReturnType<typeof mulberry32>,
) {
  ctx.fillStyle = color;
  if (kind === 'pine') {
    ctx.beginPath();
    ctx.moveTo(x, y - size * 2.4);
    ctx.lineTo(x + size * 0.5, y);
    ctx.lineTo(x - size * 0.5, y);
    ctx.closePath();
    ctx.fill();
  } else if (kind === 'corn') {
    ctx.fillRect(x - size * 0.08, y - size * 1.2, size * 0.16, size * 1.2);
  } else {
    // A broadleaf: two or three overlapping blobs on a short trunk.
    ctx.fillRect(x - size * 0.07, y - size * 0.9, size * 0.14, size * 0.9);
    for (let i = 0; i < 3; i++) {
      ctx.beginPath();
      ctx.ellipse(
        x + rng.range(-0.5, 0.5) * size,
        y - size * rng.range(1.0, 1.5),
        size * rng.range(0.4, 0.7),
        size * rng.range(0.3, 0.55),
        0,
        0,
        TAU,
      );
      ctx.fill();
    }
  }
}

/**
 * The foreground cover, drawn OVER the deer's legs. Two jobs: it puts the
 * player behind something the way a stand does, and it gives the near depth
 * band a reason to be harder than the far one.
 */
export function paintCover(w: number, h: number, palette: SitePalette, kind: string, seedText: string): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.floor(w));
  c.height = Math.max(1, Math.floor(h));
  const ctx = c.getContext('2d')!;
  const rng = mulberry32(hashSeed(seedText + ':cover'));
  ctx.fillStyle = palette.cover;
  ctx.strokeStyle = palette.cover;
  ctx.lineCap = 'round';

  const base = h;

  /**
   * A solid base band along the very bottom, then vegetation standing on it.
   *
   * The first version drew only thin stems in the bottom fifth of this canvas,
   * and the screenshot pass showed the result: a wide, flat, empty stripe of
   * ground between the herd and the HUD with nothing in it. You are supposed to
   * be looking out from behind something.
   */
  ctx.beginPath();
  ctx.moveTo(0, h);
  for (let x = 0; x <= w; x += 8) {
    const u = x / w;
    ctx.lineTo(x, base - h * (0.1 + 0.05 * Math.sin(u * 6.1 + 0.7) + 0.03 * Math.sin(u * 17.3)));
  }
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fill();

  switch (kind) {
    case 'corn': {
      for (let i = 0; i < 110; i++) {
        const x = rng.range(-0.03, 1.03) * w;
        const height = rng.range(0.42, 0.95) * h;
        const lean = rng.range(-0.05, 0.05) * w;
        ctx.lineWidth = rng.range(2, 5);
        ctx.beginPath();
        ctx.moveTo(x, base);
        ctx.quadraticCurveTo(x + lean * 0.5, base - height * 0.6, x + lean, base - height);
        ctx.stroke();
        // Leaves.
        for (let k = 0; k < 3; k++) {
          const u = 0.3 + k * 0.22;
          const lx = x + lean * u;
          const ly = base - height * u;
          const side = k % 2 === 0 ? 1 : -1;
          ctx.lineWidth = rng.range(2, 4);
          ctx.beginPath();
          ctx.moveTo(lx, ly);
          ctx.quadraticCurveTo(lx + side * height * 0.16, ly - height * 0.08, lx + side * height * 0.26, ly + height * 0.05);
          ctx.stroke();
        }
      }
      break;
    }
    case 'pine': {
      /**
       * Young pines along the bottom edge.
       *
       * This was boughs sweeping in from the left and right, which at
       * foreground scale rendered as horizontal scribbles — like feathers lying
       * on the ground. A vertical tiered triangle is unmistakably a conifer at
       * any size, and it also frames the scene the way standing in a stand of
       * pines actually does.
       */
      for (let i = 0; i < 24; i++) {
        const x = rng.range(-0.03, 1.03) * w;
        const th = rng.range(0.4, 1.0) * h;
        const halfW = th * rng.range(0.2, 0.3);
        const tiers = 5;
        const side: Array<[number, number]> = [];
        for (let k = 1; k <= tiers; k++) {
          const u = k / tiers;
          const y = base - th + th * u;
          // Each tier steps out, then tucks back in — the notched pine profile.
          side.push([halfW * u * 0.52, y - (th / tiers) * 0.5]);
          side.push([halfW * u, y]);
        }
        ctx.beginPath();
        ctx.moveTo(x, base - th);
        for (const [dx, y] of side) ctx.lineTo(x + dx, y);
        ctx.lineTo(x, base);
        for (let k = side.length - 1; k >= 0; k--) ctx.lineTo(x - side[k]![0], side[k]![1]);
        ctx.closePath();
        ctx.fill();
      }
      break;
    }
    case 'drift': {
      // Snow banked along the bottom, plus a few bare stems.
      ctx.beginPath();
      ctx.moveTo(0, base);
      for (let x = 0; x <= w; x += 6) {
        const u = x / w;
        ctx.lineTo(x, base - h * (0.28 + 0.16 * Math.sin(u * 5 + 1) + 0.07 * Math.sin(u * 13)));
      }
      ctx.lineTo(w, base);
      ctx.closePath();
      ctx.fill();
      // Bare stems standing out of the drift. Dark, because they read against
      // snow — this is the one site where the cover is lighter than the ground.
      ctx.strokeStyle = 'rgba(24,32,50,0.8)';
      for (let i = 0; i < 20; i++) {
        const x = rng.range(0, w);
        const height = rng.range(0.16, 0.44) * h;
        const root = base - h * rng.range(0.2, 0.34);
        ctx.lineWidth = rng.range(1.2, 2.4);
        ctx.beginPath();
        ctx.moveTo(x, root);
        ctx.quadraticCurveTo(x + rng.range(-14, 14), root - height * 0.6, x + rng.range(-26, 26), root - height);
        ctx.stroke();
      }
      break;
    }
    case 'thicket': {
      for (let i = 0; i < 58; i++) {
        const x = rng.range(-0.02, 1.02) * w;
        const height = rng.range(0.3, 0.85) * h;
        ctx.lineWidth = rng.range(2, 6);
        ctx.beginPath();
        ctx.moveTo(x, base);
        const midX = x + rng.range(-0.06, 0.06) * w;
        ctx.quadraticCurveTo(midX, base - height * 0.55, x + rng.range(-0.1, 0.1) * w, base - height);
        ctx.stroke();
        for (let k = 0; k < 2; k++) {
          ctx.lineWidth = rng.range(1.5, 3);
          const bx = x + rng.range(-0.03, 0.03) * w;
          const by = base - height * rng.range(0.3, 0.7);
          ctx.beginPath();
          ctx.moveTo(bx, by);
          ctx.lineTo(bx + rng.range(-0.07, 0.07) * w, by - height * 0.3);
          ctx.stroke();
        }
      }
      break;
    }
    default: {
      // Brush: clumps of grass blades.
      for (let i = 0; i < 170; i++) {
        const x = rng.range(-0.02, 1.02) * w;
        const height = rng.range(0.22, 0.78) * h;
        ctx.lineWidth = rng.range(1.5, 4);
        ctx.beginPath();
        ctx.moveTo(x, base);
        ctx.quadraticCurveTo(x + rng.range(-8, 8), base - height * 0.6, x + rng.range(-22, 22), base - height);
        ctx.stroke();
      }
    }
  }

  /**
   * A darkening wash toward the bottom edge.
   *
   * Foreground cover is the closest thing to the player and therefore the
   * least lit — it is between them and the light, not in it. Flat-filled cover
   * competed with the herd for attention; three stops of gradient push it back
   * where it belongs and cost one `fillRect` at site-change time.
   */
  const shade = ctx.createLinearGradient(0, 0, 0, h);
  shade.addColorStop(0, 'rgba(0,0,0,0)');
  shade.addColorStop(0.55, 'rgba(0,0,0,0.18)');
  shade.addColorStop(1, 'rgba(0,0,0,0.42)');
  ctx.globalCompositeOperation = 'source-atop';
  ctx.fillStyle = shade;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';

  return c;
}

// ── the crosshair ───────────────────────────────────────────────────────────

/**
 * A dark outline under a light core (ROUNDTABLE.md, A-22). A single-weight line
 * disappears over bark, over snow, and over a deer's flank — which are the only
 * three places it is ever going to be.
 */
export function drawCrosshair(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  r: number,
  color: string,
  ready: boolean,
) {
  const gap = r * 0.34;
  ctx.save();
  ctx.lineCap = 'round';

  for (const pass of [0, 1]) {
    ctx.strokeStyle = pass === 0 ? 'rgba(0,0,0,0.72)' : color;
    ctx.lineWidth = pass === 0 ? r * 0.34 : r * 0.16;

    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    ctx.stroke();

    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      ctx.beginPath();
      ctx.moveTo(x + dx * gap, y + dy * gap);
      ctx.lineTo(x + dx * r * 1.55, y + dy * r * 1.55);
      ctx.stroke();
    }
  }

  /**
   * Four corner ticks on an outer ring — the part that makes this read as a
   * gun sight rather than a cursor. Set on the diagonals, where the four main
   * arms are not, so nothing overlaps and the ring stays open at the four
   * points a deer is most likely to be crossing.
   */
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.lineWidth = r * 0.16;
  for (const pass of [0, 1]) {
    if (pass === 1) {
      ctx.strokeStyle = hexA(color, 0.75);
      ctx.lineWidth = r * 0.07;
    }
    ctx.beginPath();
    for (let i = 0; i < 4; i++) {
      const a = Math.PI / 4 + (i * Math.PI) / 2;
      ctx.moveTo(x + Math.cos(a) * r * 1.28, y + Math.sin(a) * r * 1.28);
      ctx.lineTo(x + Math.cos(a) * r * 1.62, y + Math.sin(a) * r * 1.62);
    }
    ctx.stroke();
  }

  // A centre dot only when there is a shell in the gun, so "can I shoot right
  // now" is answerable without looking away from the deer.
  if (ready) {
    ctx.fillStyle = 'rgba(0,0,0,0.6)';
    ctx.beginPath();
    ctx.arc(x, y, r * 0.22, 0, TAU);
    ctx.fill();
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(x, y, r * 0.14, 0, TAU);
    ctx.fill();
  }
  ctx.restore();
}

// ── helpers ─────────────────────────────────────────────────────────────────

function ellipse(ctx: CanvasRenderingContext2D, cx: number, cy: number, rx: number, ry: number) {
  ctx.ellipse(cx, cy, rx, ry, 0, 0, TAU);
}

function easeOut(t: number): number {
  const k = Math.max(0, Math.min(1, t));
  return 1 - (1 - k) * (1 - k);
}

/**
 * Mix a colour toward white. Used for crest rims and cloud tints, so both stay
 * in the site's own palette instead of being a hard-coded grey that looks
 * right at dawn and wrong in the snow.
 */
export function lighten(color: string, amount: number): string {
  const [r, g, b] = parseRgb(color);
  const k = Math.max(0, Math.min(1, amount));
  const up = (v: number) => Math.round(v + (255 - v) * k);
  return `rgb(${up(r)},${up(g)},${up(b)})`;
}

function parseRgb(color: string): [number, number, number] {
  if (color.startsWith('rgb')) {
    const parts = color.replace(/^rgba?\(/, '').split(',');
    return [Number(parts[0]) || 0, Number(parts[1]) || 0, Number(parts[2]) || 0];
  }
  const hex = color.replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  return [
    parseInt(full.slice(0, 2), 16) || 0,
    parseInt(full.slice(2, 4), 16) || 0,
    parseInt(full.slice(4, 6), 16) || 0,
  ];
}

/** Re-alpha a hex or rgba colour without allocating a gradient stop parser. */
export function hexA(color: string, alpha: number): string {
  if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${alpha})`);
  if (color.startsWith('rgb(')) return color.replace('rgb(', 'rgba(').replace(')', `,${alpha})`);
  const hex = color.replace('#', '');
  const full = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Seat colours. Eight hues that stay distinguishable on a dim phone. */
export const SEAT_COLORS = [
  '#ffcf4a',
  '#59d3ff',
  '#ff7a59',
  '#8ce87a',
  '#e08cff',
  '#ffffff',
  '#ff5f8d',
  '#7ce8d0',
] as const;

export function seatColor(hue: number): string {
  return SEAT_COLORS[((hue % SEAT_COLORS.length) + SEAT_COLORS.length) % SEAT_COLORS.length]!;
}
